/**
 * Die Zahlen, nach denen der Steuerberater fragt.
 *
 * WARUM ES DAS GIBT: der Vermieter tippt seine Umsätze am Jahresende ein zweites Mal
 * ab — einmal hier, einmal in der Liste für die Kanzlei. Dabei liegt alles schon da:
 * die Rechnungen sind fortlaufend nummeriert und unveränderbar, die Zahlungen haben
 * Tag und Weg, die Ortstaxe ist ein durchlaufender Posten. Zusammenzählen ist Arbeit
 * für eine Maschine.
 *
 * WAS DAS HIER NICHT IST: keine Buchhaltung und keine Steuererklärung. Es ist eine
 * Aufstellung, die eine Kanzlei prüfen kann — und die deshalb sagen muss, WORAUF sie
 * sich stützt und wo sie unsicher ist.
 *
 * ZWEI SICHTEN, WEIL ES ZWEI WAHRHEITEN GIBT:
 *
 *   `issued`  — nach Rechnungsdatum. Das ist die Soll-Versteuerung (§ 19 UStG AT,
 *               § 16 UStG DE): die Umsatzsteuer entsteht, wenn geleistet und
 *               abgerechnet wurde, unabhängig davon, ob der Gast schon gezahlt hat.
 *               Der Normalfall für eine GmbH.
 *
 *   `paid`    — nach Zahlungseingang. Das ist die Ist-Versteuerung, die viele kleine
 *               Vermieter beantragt haben, und es ist auch die Sicht der
 *               Einnahmen-Ausgaben-Rechnung.
 *
 * Welche gilt, weiß der Vermieter nicht immer — deshalb zeigt die Auswertung beide
 * nebeneinander, statt eine zu wählen und damit stillschweigend eine steuerliche
 * Aussage zu treffen.
 *
 * Reine Funktionen, kein React, kein Datenzugriff — getestet in bookkeeping.test.ts.
 */

import type {
  Booking, Invoice, InvoiceItem, ISODate, Payment, Quote,
} from '../data/model'
import { fromCents, toCents } from './money'

export interface VatRow {
  rate: number
  net: number
  vat: number
  gross: number
}

export interface PeriodSummary {
  from: ISODate
  /** Einschließlich. */
  to: ISODate

  /** Gestellte Rechnungen im Zeitraum, ohne Stornos und ohne Gutschriften. */
  invoicedGross: number
  invoicedNet: number
  invoicedVat: number
  invoiceCount: number
  /** Umsatz je Steuersatz, nach Rechnungsdatum. */
  vatRows: VatRow[]

  /** Gutschriften (Storno) im Zeitraum, als positive Zahl. */
  creditedGross: number
  creditCount: number

  /** Zahlungseingänge im Zeitraum, nach Weg getrennt. */
  receivedGross: number
  receivedByMethod: { method: string; amount: number }[]

  /** Anzahlungen, die im Zeitraum eingegangen sind. */
  prepaymentsReceived: number

  /** Was am Ende des Zeitraums noch offen ist, aus allen Rechnungen bis dahin. */
  openInvoiced: number
  openInvoiceCount: number

  /** Offene Angebote: noch nicht angenommen und noch nicht abgelaufen. */
  quotedOpenGross: number
  quotedOpenCount: number

  /** Kautionen, die am Stichtag gehalten werden. Kein Umsatz — Fremdgeld. */
  depositsHeld: number

  /**
   * Ortstaxe aus den Rechnungen des Zeitraums. Durchlaufender Posten: sie ist kein
   * Ertrag des Betriebs, sondern wird für die Gemeinde eingehoben. Sie steht deshalb
   * getrennt und ist in `invoicedNet` bereits mit 0 % Steuersatz enthalten.
   */
  touristTax: number
}

export interface BookkeepingInput {
  from: ISODate
  /** Einschließlich. */
  to: ISODate
  invoices: Invoice[]
  invoiceItems: InvoiceItem[]
  payments: Payment[]
  quotes: Quote[]
  bookings: Booking[]
  /** Heute — entscheidet, welche Angebote als abgelaufen gelten. */
  today: ISODate
}

const inRange = (d: string | null | undefined, from: ISODate, to: ISODate) =>
  Boolean(d) && d! >= from && d! <= to

/**
 * Ob eine Zeile die Ortstaxe ist.
 *
 * Seit Migration 0040 steht das im Code. Für Rechnungen davor bleibt nur das Label —
 * es ist übersetzt und war nie zum Auswerten gedacht, deshalb wird es nur als
 * Rückfall geprüft und nicht als Regel.
 */
function isTouristTax(item: InvoiceItem): boolean {
  if (item.code) return item.code === 'tourist_tax'
  const l = item.label.toLowerCase()
  return l.includes('ortstaxe') || l.includes('kurtaxe') || l.includes('tourist tax')
}

/** Rechnungen, die zählen: festgeschrieben und nicht storniert. */
function countableInvoices(invoices: Invoice[]): Invoice[] {
  return invoices.filter((i) =>
    Boolean(i.issued_at) && i.status !== 'cancelled' && i.type !== 'credit_note')
}

/**
 * Umsatz je Steuersatz und Ortstaxe für eine Menge von Rechnungen.
 *
 * Steht als eigene Funktion da, weil die Auswertung und das Deckblatt des Belegbündels
 * dieselbe Aufstellung zeigen. Zwei Kopien dieser Schleife wären zwei Zahlen, die
 * irgendwann auseinanderlaufen — und die Kanzlei legt beide Blätter nebeneinander.
 */
function vatRowsOf(
  invoices: Invoice[], invoiceItems: InvoiceItem[],
): { vatRows: VatRow[]; touristTax: number } {
  // Je Steuersatz in Cent summieren und erst am Ende zurückrechnen — sonst weicht die
  // Steueraufstellung um Cents von der Summe ab, und genau das fällt einer Kanzlei auf.
  const byRate = new Map<number, { net: number; vat: number }>()
  const ids = new Set(invoices.map((i) => i.id))
  let touristTaxCents = 0
  for (const item of invoiceItems) {
    if (!ids.has(item.invoice_id)) continue
    const cur = byRate.get(item.vat_rate) ?? { net: 0, vat: 0 }
    cur.net += toCents(item.amount_net)
    cur.vat += toCents(item.amount_vat)
    byRate.set(item.vat_rate, cur)
    // Die Ortstaxe wird an ihrem Code erkannt, nicht am Steuersatz: „0 %" haben auch
    // andere Posten, und die Kanzlei fragt nach genau dieser einen Zahl.
    if (isTouristTax(item)) touristTaxCents += toCents(item.amount_gross)
  }

  const vatRows: VatRow[] = [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rate, v]) => ({
      rate,
      net: fromCents(v.net),
      vat: fromCents(v.vat),
      gross: fromCents(v.net + v.vat),
    }))

  return { vatRows, touristTax: fromCents(touristTaxCents) }
}

export function summarise(input: BookkeepingInput): PeriodSummary {
  const { from, to, invoices, invoiceItems, payments, quotes, bookings, today } = input

  const issued = countableInvoices(invoices).filter((i) => inRange(i.issue_date, from, to))
  const { vatRows, touristTax } = vatRowsOf(issued, invoiceItems)

  const credits = invoices.filter((i) =>
    i.type === 'credit_note' && inRange(i.issue_date, from, to))

  const received = payments.filter((p) =>
    inRange(p.received_on, from, to)
    && p.kind !== 'security_deposit' && p.kind !== 'security_deposit_refund')

  const byMethod = new Map<string, number>()
  for (const p of received) {
    byMethod.set(p.method, (byMethod.get(p.method) ?? 0) + toCents(p.amount))
  }

  // Was bis zum Stichtag abgerechnet und bis dahin nicht bezahlt wurde. Nicht nur die
  // Rechnungen DES Zeitraums: eine Forderung aus dem Vorjahr ist am 31.12. immer noch
  // offen, und die Kanzlei braucht den Stand, nicht die Bewegung.
  const upToEnd = countableInvoices(invoices).filter((i) => i.issue_date <= to)
  let openCents = 0
  let openCount = 0
  for (const inv of upToEnd) {
    const paidCents = inv.booking_id
      ? payments
        .filter((p) => p.booking_id === inv.booking_id && p.received_on <= to
          && p.kind !== 'security_deposit' && p.kind !== 'security_deposit_refund')
        .reduce((a, p) => a + toCents(p.amount), 0)
      : toCents(inv.paid_amount)
    const rest = toCents(inv.total_gross) - paidCents
    if (rest > 0) { openCents += rest; openCount += 1 }
  }

  // Ein Angebot ist offen, solange es weder angenommen noch abgelehnt noch abgelaufen
  // ist. Abgelaufene stehen nicht drin: sie wecken eine Erwartung, die es nicht gibt.
  const openQuotes = quotes.filter((q) =>
    q.created_at.slice(0, 10) <= to
    && !['accepted', 'declined', 'withdrawn', 'expired'].includes(q.status)
    && (!q.valid_until || q.valid_until >= today))

  const depositsHeld = bookings
    .filter((b) => b.deposit_status === 'held' || b.deposit_status === 'partially_retained')
    .reduce((a, b) => a + toCents(b.deposit_amount), 0)

  return {
    from,
    to,
    invoicedGross: fromCents(issued.reduce((a, i) => a + toCents(i.total_gross), 0)),
    invoicedNet: fromCents(issued.reduce((a, i) => a + toCents(i.total_net), 0)),
    invoicedVat: fromCents(issued.reduce((a, i) => a + toCents(i.total_vat), 0)),
    invoiceCount: issued.length,
    vatRows,
    creditedGross: fromCents(Math.abs(credits.reduce((a, i) => a + toCents(i.total_gross), 0))),
    creditCount: credits.length,
    receivedGross: fromCents(received.reduce((a, p) => a + toCents(p.amount), 0)),
    receivedByMethod: [...byMethod.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([method, cents]) => ({ method, amount: fromCents(cents) })),
    prepaymentsReceived: fromCents(received
      .filter((p) => p.kind === 'prepayment')
      .reduce((a, p) => a + toCents(p.amount), 0)),
    openInvoiced: fromCents(openCents),
    openInvoiceCount: openCount,
    quotedOpenGross: fromCents(openQuotes.reduce((a, q) => a + toCents(q.total_gross), 0)),
    quotedOpenCount: openQuotes.length,
    depositsHeld: fromCents(depositsHeld),
    touristTax,
  }
}

/* ------------------------------------------------------------------ Belegbündel
 *
 * Eine Kanzlei will nicht die Liste, sie will die Belege. StaySuite erzeugt PDFs über
 * den Druckdialog des Browsers — also besteht das Bündel aus einem einzigen druckbaren
 * Dokument, in dem alle Belege des Zeitraums hintereinander stehen. Einmal „Als PDF
 * sichern", eine Datei, fertig.
 *
 * Hier steht nur, WELCHE Belege hineingehören, in welcher Reihenfolge, und was auf dem
 * Deckblatt zu prüfen ist. Das Papier selbst baut InvoiceBundle.tsx.
 */

/** Eine Nummer, die zwischen zwei Belegen des Bündels fehlt. */
export interface NumberGap {
  number: string
  /**
   * `elsewhere` — die Nummer gibt es, ihr Beleg ist nur außerhalb des Zeitraums
   * datiert. Das ist die häufigste und harmloseste Erklärung, und sie gehört aufs
   * Deckblatt, damit die Kanzlei nicht danach fragen muss.
   *
   * `unknown` — die Nummer kommt in den Daten dieses Betriebs überhaupt nicht vor.
   * Meistens eine gelöschte Rechnung aus der Zeit vor der Entwurfsregel; jedenfalls
   * etwas, das der Vermieter erklären können muss.
   */
  kind: 'elsewhere' | 'unknown'
  /** Bei `elsewhere`: das Rechnungsdatum, unter dem die Nummer tatsächlich liegt. */
  issueDate?: ISODate
}

/** Ein Nummernkreis, wie er im Bündel vorkommt — üblicherweise genau einer. */
export interface NumberSeries {
  /** Was vor der laufenden Zahl steht, z. B. „R-2026-". */
  prefix: string
  first: string
  last: string
  /** Belege dieses Kreises im Bündel. */
  count: number
  /** Die fehlenden Nummern, gekürzt auf ein Maß, das aufs Papier passt. */
  gaps: NumberGap[]
  /** Wie viele es insgesamt sind — `gaps` kann gekürzt sein. */
  gapCount: number
}

/** Höchstens so viele Lücken werden einzeln benannt; der Rest wird gezählt. */
const MAX_LISTED_GAPS = 40

interface ParsedNumber { prefix: string; n: number; pad: number }

/**
 * Zerlegt „R-2026-0007" in Kreis („R-2026-") und laufende Zahl (7).
 *
 * Bewusst nicht auf das Format aus `next_document_number` festgenagelt: Präfix, Jahr
 * und Stellenzahl sind je Betrieb einstellbar, und Rechnungen aus einem Vorsystem
 * tragen ohnehin, was sie tragen. Was alle gemeinsam haben, ist die laufende Zahl am
 * Ende — daran und an nichts anderem wird zerlegt.
 */
function parseNumber(number: string): ParsedNumber | null {
  const m = /^(.*?)(\d+)$/.exec(number.trim())
  if (!m || m[2] === undefined) return null
  return { prefix: m[1] ?? '', n: Number(m[2]), pad: m[2].length }
}

/**
 * Die Lückenprüfung — der Teil des Deckblatts, den eine Kanzlei zuerst ansieht.
 *
 * § 11 UStG (AT) und § 14 UStG (DE) verlangen eine fortlaufende Nummer. Fehlt eine,
 * ist das meistens ein Storno oder eine Rechnung aus einem anderen Monat — aber die
 * Kanzlei kann das nicht wissen, solange es niemand hinschreibt. Also wird zwischen
 * der kleinsten und der größten Nummer des Bündels durchgezählt und jede fehlende
 * Nummer in ALLEN Rechnungen des Betriebs nachgeschlagen, nicht nur im Zeitraum:
 * so unterscheidet sich „liegt im Januar" von „gibt es nicht".
 *
 * Geprüft wird nur INNERHALB des Bündels. Ein Monatsbündel, das bei 0007 anfängt,
 * meldet 0001 bis 0006 nicht als Lücke — die stehen im Bündel des Vormonats.
 */
export function numberSeries(inBundle: Invoice[], all: Invoice[]): NumberSeries[] {
  const known = new Map<string, Invoice>()
  for (const i of all) if (i.number) known.set(i.number, i)

  const groups = new Map<string, { pad: number; ns: number[] }>()
  for (const inv of inBundle) {
    const p = inv.number ? parseNumber(inv.number) : null
    if (!p) continue
    const g = groups.get(p.prefix) ?? { pad: p.pad, ns: [] }
    g.pad = Math.max(g.pad, p.pad)
    g.ns.push(p.n)
    groups.set(p.prefix, g)
  }

  const out: NumberSeries[] = []
  for (const [prefix, g] of [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    const ns = [...new Set(g.ns)].sort((a, b) => a - b)
    const min = Math.min(...ns)
    const max = Math.max(...ns)
    const present = new Set(ns)
    const fmt = (n: number) => prefix + String(n).padStart(g.pad, '0')

    const gaps: NumberGap[] = []
    let gapCount = 0
    // Die Schleife läuft über die Spanne, nicht über die Belege. Bei einem kaputten
    // Nummernkreis (0001 und 999999) wäre das eine Million Durchläufe im Browser des
    // Vermieters — deshalb die Obergrenze, und lieber gar keine Aussage als ein
    // hängender Tab.
    if (max - min <= 100_000) {
      for (let n = min + 1; n < max; n++) {
        if (present.has(n)) continue
        gapCount += 1
        if (gaps.length >= MAX_LISTED_GAPS) continue
        const elsewhere = known.get(fmt(n))
        gaps.push(elsewhere
          ? { number: fmt(n), kind: 'elsewhere', issueDate: elsewhere.issue_date }
          : { number: fmt(n), kind: 'unknown' })
      }
    }

    out.push({ prefix, first: fmt(min), last: fmt(max), count: ns.length, gaps, gapCount })
  }
  return out
}

/**
 * Die Belege eines Zeitraums, in der Reihenfolge, in der sie ins Bündel gehören.
 *
 * Drin ist alles, was eine Nummer trägt: Rechnungen, Gutschriften UND stornierte
 * Rechnungen. Ein Storno löscht die ursprüngliche Rechnung nicht — sie wurde gestellt,
 * sie hat eine Nummer, und wer sie weglässt, erzeugt genau die Lücke, über die die
 * Kanzlei dann stolpert. Entwürfe bleiben draußen: sie tragen keine Nummer und sind
 * damit kein Beleg, sondern ein Vorschlag.
 */
export function bundleInvoices(invoices: Invoice[], from: ISODate, to: ISODate): Invoice[] {
  return invoices
    .filter((i) => Boolean(i.issued_at) && Boolean(i.number) && inRange(i.issue_date, from, to))
    .sort((a, b) => {
      if (a.issue_date !== b.issue_date) return a.issue_date.localeCompare(b.issue_date)
      const pa = parseNumber(a.number!)
      const pb = parseNumber(b.number!)
      // Nach Zahl, nicht nach Zeichenkette: „R-9" käme sonst hinter „R-10".
      if (pa && pb && pa.prefix === pb.prefix) return pa.n - pb.n
      return a.number!.localeCompare(b.number!)
    })
}

/** Was auf dem Deckblatt des Bündels steht. */
export interface BundleSummary {
  from: ISODate
  /** Einschließlich. */
  to: ISODate
  /** Die Belege in Druckreihenfolge. */
  docs: Invoice[]
  /** Rechnungen, die zählen: festgeschrieben, nicht storniert, keine Gutschrift. */
  invoiceCount: number
  /** Davon nachträglich stornierte — im Bündel enthalten, im Umsatz nicht. */
  cancelledCount: number
  creditCount: number
  /** Umsatz je Steuersatz, wie in der Auswertung: ohne Storno, ohne Gutschrift. */
  vatRows: VatRow[]
  net: number
  vat: number
  gross: number
  /** Gutschriften als positive Zahl. */
  creditedGross: number
  /** Stornierte Rechnungen, als positive Zahl. */
  cancelledGross: number
  touristTax: number
  series: NumberSeries[]
}

export function summariseBundle(input: {
  invoices: Invoice[]
  invoiceItems: InvoiceItem[]
  from: ISODate
  to: ISODate
}): BundleSummary {
  const { invoices, invoiceItems, from, to } = input
  const docs = bundleInvoices(invoices, from, to)

  const countable = docs.filter((i) => i.status !== 'cancelled' && i.type !== 'credit_note')
  const cancelled = docs.filter((i) => i.status === 'cancelled' && i.type !== 'credit_note')
  const credits = docs.filter((i) => i.type === 'credit_note')
  const { vatRows, touristTax } = vatRowsOf(countable, invoiceItems)

  return {
    from,
    to,
    docs,
    invoiceCount: countable.length,
    cancelledCount: cancelled.length,
    creditCount: credits.length,
    vatRows,
    net: fromCents(countable.reduce((a, i) => a + toCents(i.total_net), 0)),
    vat: fromCents(countable.reduce((a, i) => a + toCents(i.total_vat), 0)),
    gross: fromCents(countable.reduce((a, i) => a + toCents(i.total_gross), 0)),
    creditedGross: fromCents(Math.abs(credits.reduce((a, i) => a + toCents(i.total_gross), 0))),
    cancelledGross: fromCents(Math.abs(cancelled.reduce((a, i) => a + toCents(i.total_gross), 0))),
    touristTax,
    series: numberSeries(docs, invoices),
  }
}

/**
 * Wie die Datei heißt, wenn das Bündel als PDF gespeichert wird.
 *
 * Im Stil der Einzelbelege (siehe `documentFileName`), nur dass hier der Betrieb mit
 * hineingehört: die Datei geht per Mail an eine Kanzlei, die zwanzig Mandanten hat,
 * und „Belege_2026.pdf" von zwanzig Absendern ist eine Datei zu viel.
 */
export function bundleFileName(business: string, from: ISODate, to: ISODate): string {
  return ['Belege', business || 'StaySuite', from, to]
    .join('_')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
}

/**
 * Die Rechnungsliste für die Kanzlei.
 *
 * WARUM DAS KEIN DATEV-BUCHUNGSSTAPEL IST — die Frage stellt sich, und die Antwort
 * gehört hierher, damit sie nicht alle zwei Jahre neu ausdiskutiert wird:
 *
 * Ein DATEV-Buchungsstapel (EXTF, Formatkategorie 21) transportiert Buchungssätze,
 * keine Belegdaten. Pflicht sind dort unter anderem Umsatz, Soll/Haben-Kennzeichen,
 * KONTO und GEGENKONTO sowie der BU-Schlüssel; im Kopfsatz stehen Berater- und
 * Mandantennummer, Sachkontenlänge und Wirtschaftsjahresbeginn. Dasselbe gilt für
 * BMD NTCS in Österreich: auch dort sind Konto und Gegenkonto Pflicht, und der
 * Steuercode ist mandantenspezifisch.
 *
 * Nichts davon weiß StaySuite. Welcher Kontenrahmen gilt (SKR03 oder SKR04, in
 * Österreich der EKR), welches Erlöskonto zur Übernachtung mit 10 % gehört und welches
 * zum Frühstück, ob der Gast auf einen Sammeldebitor oder auf ein eigenes Personenkonto
 * gebucht wird — das entscheidet die Kanzlei, nicht die Software des Vermieters.
 *
 * Geratene Konten sind dabei nicht bloß nutzlos, sondern schlechter als gar keine: der
 * Stapel sieht importierbar aus, läuft durch, und der Fehler fällt erst bei der
 * Umsatzsteuervoranmeldung auf. Ein Format, dessen Pflichtfelder man erfinden müsste,
 * wird hier nicht erzeugt. Wenn genug Vermieter danach fragen, ist der richtige erste
 * Schritt eine Kontierungsmaske, die der Steuerberater EINMAL ausfüllt — und erst
 * danach das Format.
 *
 * Was stattdessen hier steht, ist eine Liste, die eine Kanzlei ohne Rückfragen
 * verarbeiten kann: eine Zeile je Beleg, Netto und Umsatzsteuer JE STEUERSATZ in
 * eigenen Spalten (sonst muss die Kanzlei jede Rechnung von Hand aufteilen), der
 * Leistungszeitraum getrennt vom Belegdatum (bei Vermietung fast nie derselbe), und
 * der Storno-Bezug in beide Richtungen. Dazu das Belegbündel als PDF — zusammen ist
 * das genau das, was eine Kanzlei zum Buchen braucht.
 *
 * Semikolon als Trenner und Komma als Dezimalzeichen: das ist, was ein deutsches oder
 * österreichisches Excel ohne Rückfrage richtig öffnet. Ein Punkt als Dezimalzeichen
 * macht aus 1.234,50 € die Zahl 1234.50 oder 123450 — je nach Ländereinstellung, und
 * man merkt es erst in der Bilanz.
 */
export interface InvoicesCsvInput {
  invoices: Invoice[]
  invoiceItems: InvoiceItem[]
  payments: Payment[]
  bookings: Booking[]
  from: ISODate
  /** Einschließlich. */
  to: ISODate
  guestName: (id: string | null) => string
  /** Das Land des Gastes entscheidet über die Steuerbehandlung — die Kanzlei fragt. */
  guestCountry: (id: string | null) => string
  unitName: (bookingId: string | null) => string
}

const CSV_ART: Record<string, string> = {
  invoice: 'Rechnung',
  credit_note: 'Gutschrift',
  deposit: 'Anzahlungsrechnung',
  proforma: 'Proformarechnung',
}

const CSV_STATUS: Record<string, string> = {
  draft: 'Entwurf',
  sent: 'gestellt',
  partly_paid: 'teilbezahlt',
  paid: 'bezahlt',
  overdue: 'überfällig',
  cancelled: 'storniert',
}

const CSV_METHOD: Record<string, string> = {
  bank_transfer: 'Überweisung',
  cash: 'bar',
  card: 'Karte',
  stripe: 'Stripe',
  paypal: 'PayPal',
  other: 'anders',
}

export function invoicesCsv(input: InvoicesCsvInput): string {
  const {
    invoices, invoiceItems, payments, bookings, from, to,
    guestName, guestCountry, unitName,
  } = input

  const num = (n: number) => n.toFixed(2).replace('.', ',')
  const esc = (s: string) => `"${String(s ?? '').replace(/"/g, '""')}"`

  const rows = bundleInvoices(invoices, from, to)

  // Nachschlagen statt filtern: bei einem Jahresexport wäre je Zeile ein Durchlauf
  // über alle Positionen und alle Zahlungen, und das rechnet ein Telefon nicht gern.
  const itemsBy = new Map<string, InvoiceItem[]>()
  for (const it of invoiceItems) {
    const list = itemsBy.get(it.invoice_id) ?? []
    list.push(it)
    itemsBy.set(it.invoice_id, list)
  }
  const bookingById = new Map(bookings.map((b) => [b.id, b]))
  const numberById = new Map(invoices.map((i) => [i.id, i.number ?? '']))
  // Der Storno-Bezug in die zweite Richtung: welche Gutschrift hebt diese Rechnung auf.
  const cancelledBy = new Map<string, string>()
  for (const i of invoices) {
    if (i.corrects_invoice_id && i.number) cancelledBy.set(i.corrects_invoice_id, i.number)
  }

  // Welche Steuersätze im Zeitraum überhaupt vorkommen. Feste Spalten für 10 und 20
  // wären in Deutschland falsch, in Österreich unvollständig (13 % gibt es auch) und
  // bei einer Steuersatzänderung stumm falsch — also aus den Daten gelesen.
  const ratesSeen = new Set<number>()
  for (const inv of rows) {
    for (const it of itemsBy.get(inv.id) ?? []) ratesSeen.add(it.vat_rate)
  }
  const rates = [...ratesSeen].sort((a, b) => a - b)

  const head = [
    'Belegnummer', 'Belegdatum', 'Belegart', 'Storno zu', 'Storniert durch',
    'Leistung von', 'Leistung bis', 'Einheit', 'Buchung',
    'Empfänger', 'Land',
    'Netto', 'USt.', 'Brutto',
    ...rates.flatMap((r) => [`Netto ${r} %`, `USt. ${r} %`]),
    'davon Ortstaxe',
    'Währung', 'Bezahlt am', 'Zahlungsweg', 'Bezahlt', 'Offen', 'Status',
  ].map(esc).join(';')

  // Für die Summenzeile: in Cent addieren, damit sie mit der Auswertung übereinstimmt.
  const totals = { net: 0, vat: 0, gross: 0, paid: 0, open: 0, tax: 0 }
  const rateTotals = new Map<number, { net: number; vat: number }>()

  const body = rows.map((inv) => {
    const items = itemsBy.get(inv.id) ?? []
    const booking = inv.booking_id ? bookingById.get(inv.booking_id) : undefined

    const relevant = payments.filter((p) => p.invoice_id === inv.id
      || (inv.booking_id && p.booking_id === inv.booking_id
          && p.kind !== 'security_deposit' && p.kind !== 'security_deposit_refund'))
    const last = [...relevant].sort((a, b) => a.received_on.localeCompare(b.received_on)).at(-1)
    const paidCents = relevant.reduce((a, p) => a + toCents(p.amount), 0)
    const openCents = toCents(inv.total_gross) - paidCents

    const byRate = new Map<number, { net: number; vat: number }>()
    let taxCents = 0
    for (const it of items) {
      const cur = byRate.get(it.vat_rate) ?? { net: 0, vat: 0 }
      cur.net += toCents(it.amount_net)
      cur.vat += toCents(it.amount_vat)
      byRate.set(it.vat_rate, cur)
      if (isTouristTax(it)) taxCents += toCents(it.amount_gross)
    }

    // Was in die Summenzeile zählt: dasselbe wie in der Auswertung und auf dem
    // Deckblatt des Bündels — festgeschrieben, nicht storniert, keine Gutschrift.
    //
    // Ein Storno besteht immer aus zwei Belegen: der aufgehobenen Rechnung und der
    // Gutschrift dagegen. Beide stehen in der Liste, weil sie Nummern tragen, aber
    // nur einer von beiden darf aus der Summe fallen — sonst wird derselbe Betrag
    // zweimal abgezogen. Also fallen beide raus, und die Zeile sagt das auch.
    const counts = inv.status !== 'cancelled' && inv.type !== 'credit_note'
    if (counts) {
      totals.net += toCents(inv.total_net)
      totals.vat += toCents(inv.total_vat)
      totals.gross += toCents(inv.total_gross)
      totals.tax += taxCents
      totals.paid += paidCents
      totals.open += Math.max(0, openCents)
      for (const [rate, v] of byRate) {
        const cur = rateTotals.get(rate) ?? { net: 0, vat: 0 }
        cur.net += v.net
        cur.vat += v.vat
        rateTotals.set(rate, cur)
      }
    }

    return [
      esc(inv.number ?? ''),
      esc(inv.issue_date),
      esc(CSV_ART[inv.type] ?? inv.type),
      esc(inv.corrects_invoice_id ? numberById.get(inv.corrects_invoice_id) ?? '' : ''),
      esc(cancelledBy.get(inv.id) ?? ''),
      // Der Leistungszeitraum steht auf der Rechnung, wenn er dort steht; sonst ist er
      // der Aufenthalt. Bei einer Vermietung ist er fast nie das Belegdatum, und die
      // Umsatzsteuer hängt an ihm, nicht am Tag des Ausdrucks.
      esc(inv.service_from ?? booking?.arrival ?? ''),
      esc(inv.service_to ?? booking?.departure ?? ''),
      esc(unitName(inv.booking_id)),
      esc(booking?.reference ?? ''),
      esc(guestName(inv.guest_id)),
      esc(guestCountry(inv.guest_id)),
      num(inv.total_net),
      num(inv.total_vat),
      num(inv.total_gross),
      ...rates.flatMap((r) => {
        const v = byRate.get(r)
        return [num(fromCents(v?.net ?? 0)), num(fromCents(v?.vat ?? 0))]
      }),
      num(fromCents(taxCents)),
      esc(inv.currency),
      esc(last?.received_on ?? ''),
      esc(last ? CSV_METHOD[last.method] ?? last.method : ''),
      num(fromCents(paidCents)),
      num(fromCents(Math.max(0, openCents))),
      esc(CSV_STATUS[inv.status] ?? inv.status),
    ].join(';')
  })

  // Die Summenzeile. Eine Kanzlei prüft als Erstes, ob die Summe zum Bankauszug passt,
  // und rechnet sie sonst von Hand nach. Sie steht am Ende und ist als „Summe"
  // beschriftet, damit niemand sie für einen Beleg hält.
  const sumRow = [
    esc('Summe'), esc(''), esc(''), esc(''), esc(''), esc(from), esc(to), esc(''), esc(''),
    esc(`${rows.length} Belege`), esc(''),
    num(fromCents(totals.net)), num(fromCents(totals.vat)), num(fromCents(totals.gross)),
    ...rates.flatMap((r) => {
      const v = rateTotals.get(r)
      return [num(fromCents(v?.net ?? 0)), num(fromCents(v?.vat ?? 0))]
    }),
    num(fromCents(totals.tax)),
    esc(''), esc(''), esc(''),
    num(fromCents(totals.paid)), num(fromCents(totals.open)),
    esc('ohne Stornos und Gutschriften'),
  ].join(';')

  // BOM voran, sonst zeigt Excel „Straße" als „StraÃŸe".
  return '﻿' + [head, ...body, sumRow].join('\r\n') + '\r\n'
}

/**
 * Wie die Liste heißt. Der Zeitraum gehört in den Dateinamen — die Kanzlei bekommt im
 * Januar drei davon und muss sie ohne Öffnen auseinanderhalten können.
 */
export function invoicesCsvFileName(business: string, from: ISODate, to: ISODate): string {
  return ['Rechnungsliste', business || 'StaySuite', from, to]
    .join('_')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_') + '.csv'
}
